import { useEffect, useRef, useState } from "react";

const DEFAULT_PAUSE_AFTER_HIDDEN_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Call `callback` on an interval while `enabled` is true.
 * Clears on disable/unmount.
 *
 * If the browser tab stays hidden for `pauseAfterHiddenMs` (default 10 min),
 * polling pauses until the user is active again (tab visible / focus / pointer / key).
 */
export function useIntervalWhen(
  callback,
  delayMs,
  enabled = true,
  { pauseAfterHiddenMs = DEFAULT_PAUSE_AFTER_HIDDEN_MS } = {}
) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const [pausedForInactivity, setPausedForInactivity] = useState(false);
  const hiddenSinceRef = useRef(null);
  const hiddenTimerRef = useRef(null);

  const clearHiddenTimer = () => {
    if (hiddenTimerRef.current != null) {
      clearTimeout(hiddenTimerRef.current);
      hiddenTimerRef.current = null;
    }
  };

  const markActive = () => {
    hiddenSinceRef.current = null;
    clearHiddenTimer();
    setPausedForInactivity(false);
  };

  // Track tab visibility / user activity → pause after long inactivity, resume on activity
  useEffect(() => {
    if (!enabled || typeof document === "undefined") return undefined;

    const scheduleHiddenPause = () => {
      if (document.visibilityState !== "hidden") return;
      if (hiddenSinceRef.current == null) {
        hiddenSinceRef.current = Date.now();
      }
      clearHiddenTimer();
      const elapsed = Date.now() - (hiddenSinceRef.current || Date.now());
      const remaining = Math.max(0, pauseAfterHiddenMs - elapsed);
      hiddenTimerRef.current = setTimeout(() => {
        if (document.visibilityState === "hidden") {
          setPausedForInactivity(true);
        }
      }, remaining);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        scheduleHiddenPause();
      } else {
        markActive();
      }
    };

    const onUserActivity = () => {
      if (document.visibilityState === "visible") {
        markActive();
      }
    };

    if (document.visibilityState === "hidden") {
      scheduleHiddenPause();
    }

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onUserActivity);
    window.addEventListener("pointerdown", onUserActivity);
    window.addEventListener("keydown", onUserActivity);

    return () => {
      clearHiddenTimer();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onUserActivity);
      window.removeEventListener("pointerdown", onUserActivity);
      window.removeEventListener("keydown", onUserActivity);
    };
  }, [enabled, pauseAfterHiddenMs]);

  // Interval itself
  useEffect(() => {
    if (!enabled || pausedForInactivity || delayMs == null || delayMs <= 0) {
      return undefined;
    }

    const id = setInterval(() => {
      void callbackRef.current();
    }, delayMs);

    return () => clearInterval(id);
  }, [delayMs, enabled, pausedForInactivity]);

  // When resuming from inactivity pause, refresh once immediately
  const wasPausedRef = useRef(false);
  useEffect(() => {
    if (!enabled) {
      wasPausedRef.current = false;
      return;
    }
    if (pausedForInactivity) {
      wasPausedRef.current = true;
      return;
    }
    if (wasPausedRef.current) {
      wasPausedRef.current = false;
      void callbackRef.current();
    }
  }, [enabled, pausedForInactivity]);
}
